import React, { useState, useEffect } from 'react';
import { bookingAPI } from '../services/api';

const STATUS_COLORS = {
  pending: {
    bg: 'rgba(245, 158, 11, 0.15)',
    bgLight: 'rgba(245, 158, 11, 0.08)',
    border: '#f59e0b',
    text: '#fbbf24',
    textLight: '#d97706'
  },
  confirmed: {
    bg: 'rgba(59, 130, 246, 0.15)',
    bgLight: 'rgba(59, 130, 246, 0.08)',
    border: '#3b82f6',
    text: '#60a5fa',
    textLight: '#3b82f6'
  },
  in_progress: {
    bg: 'rgba(78, 205, 196, 0.15)',
    bgLight: 'rgba(78, 205, 196, 0.08)',
    border: '#4ecdc4',
    text: '#4ecdc4',
    textLight: '#22c1c3'
  },
  completed: {
    bg: 'rgba(26, 40, 17, 0.15)',
    bgLight: 'rgba(26, 40, 17, 0.08)',
    border: '#1A2811',
    text: '#4A8C18',
    textLight: '#1A2811'
  },
  cancelled: {
    bg: 'rgba(239, 68, 68, 0.15)',
    bgLight: 'rgba(239, 68, 68, 0.08)',
    border: '#ef4444',
    text: '#f87171',
    textLight: '#dc2626'
  }
};

const DAY_LABELS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

const startOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff));
};

const endOfWeek = (date) => {
  const start = startOfWeek(date);
  return new Date(start.getTime() + 6 * 24 * 60 * 60 * 1000);
};

const formatTime = (timeStr) => {
  if (!timeStr) return '';
  const [hours, minutes] = String(timeStr).split(':');
  return `${hours || '00'}:${minutes || '00'}`;
};

const getPrimaryLabel = (booking) => {
  if (booking?.customer_name && booking.customer_name.trim()) {
    return booking.customer_name.trim();
  }

  const userName = [booking?.user?.firstname, booking?.user?.surname].filter(Boolean).join(' ').trim();
  if (userName) {
    return userName;
  }

  const bikeName = [booking?.bike?.brand, booking?.bike?.model].filter(Boolean).join(' ').trim();
  if (bikeName) {
    return bikeName;
  }

  if (booking?.service_template?.name) {
    return booking.service_template.name;
  }

  return 'Booking';
};

const getSecondaryLabel = (booking) => {
  if (booking?.service_template?.name) {
    return booking.service_template.name;
  }

  const bikeName = [booking?.bike?.brand, booking?.bike?.model].filter(Boolean).join(' ').trim();
  return bikeName;
};

function BookingCalendar({ companyId, onBookingSelect, darkMode = true }) {
  const [bookings, setBookings] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const weekStart = startOfWeek(currentDate);
  const weekEnd = endOfWeek(currentDate);

  useEffect(() => {
    if (companyId) {
      fetchBookings();
    }
  }, [companyId, currentDate]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const response = await bookingAPI.getAllBookings({
        date_from: weekStart.toISOString().split('T')[0],
        date_to: weekEnd.toISOString().split('T')[0]
      });
      setBookings(response.data.bookings || []);
      setError('');
    } catch (err) {
      setError('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const weekDays = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(weekStart);
    date.setDate(weekStart.getDate() + i);
    weekDays.push(date);
  }

  const getBookingsForDay = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return bookings
      .filter((b) => b.booking_date === dateStr)
      .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
  };

  const previousWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentDate(newDate);
  };

  const nextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  if (loading) {
    return <div className="loading">Loading calendar...</div>;
  }

  const isMobileView = typeof window !== 'undefined' && window.innerWidth <= 768;

  return (
    <div style={{ width: '100%' }}>
      {error && <div className="error">{error}</div>}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <button onClick={previousWeek} className="btn btn-secondary btn-sm" style={{ minHeight: '40px' }}>
          {'<'} Previous
        </button>
        <h3 style={{ margin: 0, fontSize: isMobileView ? '14px' : '16px', textAlign: 'center', flex: 1 }}>
          {weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {' '}
          {weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </h3>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={goToToday} className="btn btn-secondary btn-sm" style={{ minHeight: '40px' }}>
            Today
          </button>
          <button onClick={nextWeek} className="btn btn-secondary btn-sm" style={{ minHeight: '40px' }}>
            Next {'>'}
          </button>
        </div>
      </div>

      {isMobileView ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {weekDays.map((date, index) => {
            const dayBookings = getBookingsForDay(date);
            const isToday = date.toDateString() === new Date().toDateString();

            return (
              <div
                key={index}
                style={{
                  background: darkMode ? 'rgba(255,255,255,0.03)' : '#ffffff',
                  border: isToday
                    ? '1px solid rgba(74, 140, 24, 0.7)'
                    : darkMode
                      ? '1px solid rgba(255,255,255,0.06)'
                      : '1px solid rgba(0,0,0,0.1)',
                  borderRadius: '12px',
                  padding: '12px'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '10px' }}>
                  <div style={{ fontWeight: '700', fontSize: '13px', letterSpacing: '0.02em', color: isToday ? '#4A8C18' : darkMode ? 'rgba(255,255,255,0.82)' : 'rgba(0,0,0,0.8)' }}>
                    {DAY_LABELS[index]}
                  </div>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: darkMode ? '#ffffff' : '#1a1a1a' }}>
                    {date.getDate()}
                  </div>
                </div>

                {dayBookings.length === 0 ? (
                  <div style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)' }}>
                    No bookings
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {dayBookings.map((booking) => {
                      const colors = STATUS_COLORS[booking.status] || STATUS_COLORS.pending;
                      const secondary = getSecondaryLabel(booking);

                      return (
                        <div
                          key={booking.id}
                          onClick={() => onBookingSelect && onBookingSelect(booking)}
                          style={{
                            padding: '8px 10px',
                            borderRadius: '8px',
                            cursor: onBookingSelect ? 'pointer' : 'default',
                            background: darkMode ? colors.bg : colors.bgLight,
                            borderLeft: `3px solid ${colors.border}`,
                            color: darkMode ? colors.text : colors.textLight,
                            opacity: booking.status === 'cancelled' ? 0.65 : 1,
                            overflow: 'hidden'
                          }}
                        >
                          <div style={{ fontWeight: '600', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {getPrimaryLabel(booking)}
                          </div>
                          <div style={{ fontSize: '11px', opacity: 0.9, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {formatTime(booking.start_time)}-{formatTime(booking.end_time)}
                            {secondary ? ` | ${secondary}` : ''}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, minmax(120px, 1fr))',
              gap: '1px',
              minWidth: '980px',
              borderRadius: '16px',
              overflow: 'hidden',
              border: darkMode ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(0,0,0,0.1)',
              background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)'
            }}
          >
            {weekDays.map((date, index) => {
              const dayBookings = getBookingsForDay(date);
              const isToday = date.toDateString() === new Date().toDateString();

              return (
                <div
                  key={index}
                  style={{
                    minHeight: '290px',
                    background: darkMode
                      ? isToday
                        ? 'rgba(61, 90, 32, 0.08)'
                        : 'rgba(255,255,255,0.03)'
                      : '#ffffff',
                    padding: '10px'
                  }}
                >
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{ fontSize: '12px', letterSpacing: '0.04em', fontWeight: '600', color: isToday ? '#4A8C18' : darkMode ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)' }}>
                      {DAY_LABELS[index]}
                    </div>
                    <div style={{ fontSize: '34px', lineHeight: 1, fontWeight: '700', color: darkMode ? '#ffffff' : '#1a1a1a' }}>
                      {date.getDate()}
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '220px', overflowY: 'auto', paddingRight: '2px' }}>
                    {dayBookings.length === 0 && (
                      <div style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.38)' : 'rgba(0,0,0,0.4)' }}>
                        No bookings
                      </div>
                    )}

                    {dayBookings.map((booking) => {
                      const colors = STATUS_COLORS[booking.status] || STATUS_COLORS.pending;
                      const secondary = getSecondaryLabel(booking);

                      return (
                        <div
                          key={booking.id}
                          onClick={() => onBookingSelect && onBookingSelect(booking)}
                          style={{
                            padding: '8px 9px',
                            borderRadius: '8px',
                            cursor: onBookingSelect ? 'pointer' : 'default',
                            background: darkMode ? colors.bg : colors.bgLight,
                            borderLeft: `3px solid ${colors.border}`,
                            color: darkMode ? colors.text : colors.textLight,
                            opacity: booking.status === 'cancelled' ? 0.65 : 1,
                            overflow: 'hidden'
                          }}
                          title={`${getPrimaryLabel(booking)} | ${formatTime(booking.start_time)}-${formatTime(booking.end_time)}`}
                        >
                          <div style={{ fontWeight: '600', fontSize: '12px', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {getPrimaryLabel(booking)}
                          </div>
                          <div style={{ fontSize: '10px', lineHeight: 1.2, opacity: 0.9, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {formatTime(booking.start_time)}-{formatTime(booking.end_time)}
                            {secondary ? ` | ${secondary}` : ''}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default BookingCalendar;
